import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";

import { Form, Passwordbox, Textbox } from "capitalrx-components-2.0/lib/forms";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";

import { cognitoUpdatePassword, validateForgotPasswordToken } from "@/App/api/login";
import { ClientAwareLink, ThemedSubmitButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";

interface UpdatePasswordForm {
  readonly confirmationCode: string;
  readonly password: string;
  readonly confirmPassword: string;
}

export const UpdatePasswordPage: FC = () => {

  const { t: translate } = useTranslation();

  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const token = searchParams.get("token");
  const navigate = useNavigateClient();
  const [loading, setLoading] = useState(true);
  const alert = useAlert();
  const form = useForm<UpdatePasswordForm>();

  const validatePasswordMatch = () => {
    const password = form.watch("password");
    const confirmPassword = form.watch("confirmPassword");
    if (password != confirmPassword) {
      return translate("UPDATE_PASSWORD_PAGE.passwordDoNotMatch");
    } else {
      return undefined;
    }
  };

  useEffect(() => {
    if (!email || !token) {
      navigate("/login");
    } else if (email && token) {
      validateForgotPasswordToken({
        identity: email,
        token: token,
      })
        .then(() => {
          setLoading(false);
        })
        .catch((e) => {
          const message = e?.data?.message || translate("UPDATE_PASSWORD_PAGE.providedTokenInvalid");
          alert({
            message: message,
            style: "toast",
            type: "error",
          });
          navigate("/forgot-password");
        });
    }
  }, [email, token]);

  const submitForm = async (formData: UpdatePasswordForm) => {
    try {
      await cognitoUpdatePassword({
        identity: email,
        password: formData.password,
        confirmation_code: formData.confirmationCode,
      });
      alert({
        message: translate("UPDATE_PASSWORD_PAGE.passwordSuccessfullyUpdated"),
        style: "toast",
        type: "success",
      });
      navigate("/login");
    } catch (e) {
      if(e?.name === "InvalidPasswordException") {
        alert({
          message: translate("UPDATE_PASSWORD_PAGE.invalidPasswordException"),
          style: "banner",
          type: "error",
          timeout: 10000,
        });
      } else if (e?.name === "PasswordHistoryPolicyViolationException") {
        alert({
          message: translate("UPDATE_PASSWORD_PAGE.passwordHistoryPolicyViolationException"),
          style: "banner",
          type: "error",
          timeout: 10000,
        });
      } else {
        alert({
          message: translate("UPDATE_PASSWORD_PAGE.passwordFailed"),
          style: "toast",
          type: "error",
        });
      }
    }
  };

  return (
    <Form form={form} loading={loading} onSubmit={submitForm} testId="update-password">
      <Grid>
        <Grid.Span columns={12}>
          <Subheading textAlign="center">{translate("UPDATE_PASSWORD_PAGE.confirmationCode")}</Subheading>
        </Grid.Span>
        <Grid.Span columns={12}>
          <Paragraph textAlign="center">{translate("UPDATE_PASSWORD_PAGE.enterConfirmationCode")}</Paragraph>
        </Grid.Span>

        <Grid.Span columns={12}>
          <Textbox.Field testId="confirmation-code" required placeholder={translate("UPDATE_PASSWORD_PAGE.confirmationCode")} label={translate("UPDATE_PASSWORD_PAGE.confirmationCode")} name="confirmationCode" />
        </Grid.Span>

        <Grid.Span columns={12}>
          <Subheading textAlign="center">{translate("UPDATE_PASSWORD_PAGE.createNewPassword")}</Subheading>
        </Grid.Span>
        <Grid.Span columns={12}>
          <Paragraph textAlign="center">
            {translate("UPDATE_PASSWORD_PAGE.passwordPolicy")}
          </Paragraph>
        </Grid.Span>
        <Grid.Span columns={12}>
          <Passwordbox.Field testId="password" required placeholder={translate("UPDATE_PASSWORD_PAGE.password")} label={translate("UPDATE_PASSWORD_PAGE.password")} name="password" />
        </Grid.Span>
        <Grid.Span columns={12}>
          <Passwordbox.Field
            testId="confirm-password"
            required
            validator={validatePasswordMatch}
            placeholder={translate("UPDATE_PASSWORD_PAGE.confirmPassword")}
            label={translate("UPDATE_PASSWORD_PAGE.confirmPassword")}
            name="confirmPassword"
          />
        </Grid.Span>

        <Grid.Span columns={12}>
          <ThemedSubmitButton testId="save-password" icon="arrow-right" iconPosition="right">
            {translate("UPDATE_PASSWORD_PAGE.savePassword")}
          </ThemedSubmitButton>
        </Grid.Span>

        <Shelf>
          <Shelf.Spacer />
          <ClientAwareLink testId="to-login" to="/login">{translate("UPDATE_PASSWORD_PAGE.backToLogin")}</ClientAwareLink>
          <Shelf.Spacer />
        </Shelf>
      </Grid>
    </Form>
  );
};
